import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, ChevronDown, ChevronUp, ExternalLink, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface UntouchedLead {
  id: string;
  name: string;
  company: string;
  stage: string;
  priority: string;
  hoursSinceActivity: number;
  daysSinceActivity: number;
  urgency: 'critical' | 'high' | 'medium' | 'low';
  lastActivity: string;
  assignedToUser?: {
    id: string;
    name: string;
    email: string;
  };
}

interface UntouchedLeadsResponse {
  success: boolean;
  data: {
    count: number;
    leads: UntouchedLead[];
    summary: {
      total: number;
      critical: number;
      high: number;
      medium: number;
    };
  };
  meta: {
    threshold: string;
    thresholdDate: string;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const UntouchedLeadsAlert: React.FC = () => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const { data, isLoading, error } = useQuery<UntouchedLeadsResponse>({
    queryKey: ['untouchedLeads'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/lead-metrics/dashboard/untouched-leads?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch untouched leads');
      return response.json();
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const handleLeadClick = (leadId: string) => {
    navigate(`/leads/${leadId}`);
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getUrgencyBorderColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'border-red-500';
      case 'high': return 'border-orange-500';
      case 'medium': return 'border-yellow-500';
      default: return 'border-gray-500';
    }
  };

  // Don't render if dismissed, no data, or no untouched leads
  if (isDismissed || isLoading || error || !data?.data?.count) {
    return null;
  }

  const { count, leads, summary } = data.data;
  const hasCritical = summary.critical > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="mb-6"
    >
      <Card className={`border-2 ${hasCritical ? 'border-red-500 bg-red-50' : 'border-orange-400 bg-orange-50'}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className={`h-5 w-5 ${hasCritical ? 'text-red-600' : 'text-orange-600'}`} />
              <span className={hasCritical ? 'text-red-800' : 'text-orange-800'}>
                {count} Lead{count !== 1 ? 's' : ''} Need Attention
              </span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {summary.critical > 0 && (
                  <Badge className="bg-red-500 text-white text-xs">
                    {summary.critical} Critical
                  </Badge>
                )}
                {summary.high > 0 && (
                  <Badge className="bg-orange-500 text-white text-xs">
                    {summary.high} High
                  </Badge>
                )}
                {summary.medium > 0 && (
                  <Badge className="bg-yellow-500 text-white text-xs">
                    {summary.medium} Medium
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-2"
              >
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsDismissed(true)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
          <p className={`text-sm ${hasCritical ? 'text-red-700' : 'text-orange-700'} mt-1`}>
            These leads haven't been touched in 72+ hours. Click to view and take action.
          </p>
        </CardHeader>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <CardContent className="pt-0">
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {leads.map((lead) => (
                    <div
                      key={lead.id}
                      onClick={() => handleLeadClick(lead.id)}
                      className={`flex items-center justify-between p-3 bg-white rounded-lg border-l-4 ${getUrgencyBorderColor(lead.urgency)} cursor-pointer hover:shadow-md transition-all`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">{lead.name}</span>
                          <Badge variant="outline" className="text-xs">{lead.stage}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 truncate">{lead.company}</p>
                        {lead.assignedToUser && (
                          <p className="text-xs text-gray-500">Assigned to: {lead.assignedToUser.name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <div className="text-right">
                          <Badge className={`${getUrgencyColor(lead.urgency)} text-xs`}>
                            <Clock className="h-3 w-3 mr-1" />
                            {lead.daysSinceActivity > 0
                              ? `${lead.daysSinceActivity}d ${lead.hoursSinceActivity % 24}h`
                              : `${lead.hoursSinceActivity}h`
                            }
                          </Badge>
                        </div>
                        <ExternalLink className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>

                {data.meta.totalPages > 1 && (
                  <div className="mt-3 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/pipeline?filter=untouched')}
                    >
                      View All {count} Untouched Leads
                    </Button>
                  </div>
                )}
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>

        {!isExpanded && (
          <CardContent className="pt-0 pb-3">
            <Button
              variant="link"
              className={`p-0 h-auto ${hasCritical ? 'text-red-700' : 'text-orange-700'}`}
              onClick={() => setIsExpanded(true)}
            >
              Click to see details
            </Button>
          </CardContent>
        )}
      </Card>
    </motion.div>
  );
};
